import React, { useState } from 'react'

import { Alert, Box, Link, Text } from '@factorialco/deprecated-design-system'
import { EditLeavePageDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { convertHoursToTimeFormat } from 'modules/core/lib/formatTime'
import hasFeature from 'modules/core/lib/hasFeature'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'
import { DelegateApproverSelector } from 'modules/approvals/components/DelegateApproverSelector'
import { useAccessApprovalFlows } from 'modules/approvals/hooks/useAccessApprovalFlows'
import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import FranceCalendarWarnings from 'modules/timeoff/components/FranceCalendarWarnings'
import Content from 'modules/timeoff/components/LeaveContent'
import LeaveInfo from 'modules/timeoff/components/LeaveInfo'
import useFetchStats from 'modules/timeoff/hooks/useFetchStats'
import useLabourUnits from 'modules/timeoff/hooks/useLabourUnits'
import allowances from 'modules/timeoff/store/collections/allowances'
import leaveHistories from 'modules/timeoff/store/collections/leaveHistories'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import Allowance from 'modules/timeoff/store/models/Allowance'
import type LeaveHistory from 'modules/timeoff/store/models/LeaveHistory'

import ActionButtons from './ActionButtons'
import { isAuthorSameAsOwner } from './lib/isAuthorSameAsOwner'
import useCustomFields from './useCustomFields'
import useForms from './useForms'
import useLeaveData from './useLeaveData'
import { useMaterializedApprovalFlowApproverCheck } from './useMaterializedApprovalFlowApproverCheck'
import useModalCopies from './useModalCopies'
import useUpdateLeave from './useUpdateLeave'

// TODO: This is crazy, this component is re-rendering hundreds of times
interface Props {
  leaveId: number
  onClose: () => void
  onChange?: () => void
  leaveYear?: number
  onReject: () => void
  fromCalendar?: boolean
  contractVersions: ContractVersions
  loading: boolean
  fromShifts?: boolean
}

const EditLeavePage = ({
  leaveYear,
  leaveId,
  onClose,
  onChange,
  onReject,
  fromCalendar = false,
  contractVersions,
  loading,
  fromShifts = false,
}: Props) => {
  useAsyncData(() => {
    if (!fromCalendar) return Promise.resolve()

    return allowances.fetch()
  }, [fromCalendar])

  const year = useMemo(() => leaveYear || moment().year(), [leaveYear])
  const {
    leave,
    leaveConfirmationDetails,
    loading: leaveDataLoading,
    employee,
    permissions,
    medicalLeave,
  } = useLeaveData({ leaveId })

  const { data, loading: pageLoading } = useQuery(EditLeavePageDocument, {
    variables: { id: leaveId },
  })

  const electronicSickLeaveEmployee = data?.timeoff.leave?.employee
  const delegatedApprover = data?.timeoff.leave?.delegatedApproverAccess

  const { fetchStats } = useFetchStats()

  const { form, documentForm } = useForms({
    leave,
    effectiveOn: medicalLeave.medicalEffectiveOn,
  })

  const labourUnits = useLabourUnits({
    form,
  })

  const customFields = useCustomFields({
    leave,
    employee,
    contractVersions,
  })

  const { onSubmit, updateLoading } = useUpdateLeave({
    year,
    form,
    documentForm,
    leave,
    employee,
    fetchStats,
    canUpdateLeave: permissions.update,
    getCustomFieldsValues: customFields.getValues,
    onChange,
    closePortal: onClose,
  })

  const { title, description } = useModalCopies({
    form,
    leave,
    employee,
    canApprove: permissions.approve,
  })

  const onFetchStats = useCallback(() => {
    fetchStats(leave)
  }, [leave.id])

  const [hasNewDocuments, setHasNewDocuments] = useState(false)

  const onDocumentDropped = () => {
    setHasNewDocuments(true)
  }

  const formLeaveId = form.get('leave_type_id').value

  const leaveType = leaveTypes.get(+formLeaveId)

  const leaveIdAllowance = employee ? leaveType?.getAllowanceForEmployee(employee) : null

  const {
    company: { subscription },
    access,
    canManageTimeoff,
  } = useCurrentSession()

  const { loading: loadingApprovalFlows, approvalFlows } = useAccessApprovalFlows(
    'timeoff',
    access.id
  )

  const {
    loading: isCurrentApproverLoading,
    isCurrentApprover,
    approvalFlowId,
  } = useMaterializedApprovalFlowApproverCheck({
    resourceId: leave.id,
    resourceType: 'Timeoff::Leave',
  })

  const isLoading =
    leaveDataLoading ||
    updateLoading ||
    isCurrentApproverLoading ||
    loadingApprovalFlows ||
    pageLoading

  const timeoffSupervisor = employee
    ? canManageTimeoff ||
      employee.isTimeoffSupervisedByCurrent() ||
      employee.isSupervisedByCurrent()
    : false

  const labourUnitsHours = labourUnits.label === Allowance.ALLOWANCE_TYPES.hours
  const hoursText = convertHoursToTimeFormat(labourUnits.value, true)
  const daysText = i18n.tp('time_off.days_with_years', {
    count: labourUnits.value,
  })
  const timeTaken = labourUnitsHours ? hoursText : daysText

  useEffect(() => {
    const isreviewTimeOffRequest = permissions.approve

    if (isreviewTimeOffRequest) {
      track(trackingEvents.timeoff.reviewTimeOffRequestStart, {
        from_shifts: fromShifts,
      })
    }
  }, [permissions.approve, fromShifts])

  const isSubmitDisabled = () => {
    const isDetailsRequired = leaveType?.attributes.details_required
    const hasLeaveDescription: string = form.get('description').value
    const halfDay = form.get('half_day').value
    const hourAmount = form.get('hours_amount_in_cents').value

    if (isDetailsRequired) {
      return isLoading || !((form.isDirty || hasNewDocuments) && hasLeaveDescription)
    }

    const absurdStartTime = !form.startTimeHasValue && !halfDay && !!hourAmount

    return isLoading || !(form.isDirty || hasNewDocuments) || absurdStartTime
  }

  const [leaveHistory, setLeaveHistory] = React.useState<LeaveHistory | null>(null)
  const [authorName, setAuthorName] = React.useState<string | null>(null)
  const [authorId, setAuthorId] = React.useState<number | null>(null)
  const [authorType, setAuthorType] = React.useState<'Access' | 'User' | null>(null)

  useEffect(() => {
    const fetchLeaveHistory = async () => {
      try {
        const fetchLeaveHistories = await leaveHistories.fetch({
          data: { leave_id: leave.id },
        })
        const data = fetchLeaveHistories ? fetchLeaveHistories[0] : null
        setLeaveHistory(data)
        setAuthorName(data.author_name)
        setAuthorId(data.author_id)
        setAuthorType(data.author_type)
      } catch (error) {}
    }
    fetchLeaveHistory()
  }, [leave.id])

  // TODO: it's not enough to check the access id since author in leave history is a polymorphic relationship.
  // Need to be fixed after backend exposes the author type (not critical for now)
  const canSeeRequester =
    leaveHistory != null &&
    (canManageTimeoff ||
      !isAuthorSameAsOwner({
        authorID: authorId,
        authorType: authorType,
        employee: employee,
      }))

  const canDelegateApprover =
    !hasFeature('dev_disable_delegate_approval') &&
    (employee?.policy.can('approveLeaves') || approvalFlows.length > 0)

  const rejectionReason = leave.attributes.reason

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={onClose}>
      <Modal
        illustration="calendar"
        title={title}
        description={description}
        onSubmit={isLoading ? undefined : onSubmit}
        onClose={onClose}
        footer={
          !medicalLeave.isMedical &&
          (!permissions.readOnly || isCurrentApprover) && (
            <ActionButtons
              form={form}
              leave={leave}
              onFetchStats={onFetchStats}
              onReject={onReject}
              onChange={(...args) => {
                if (onChange) {
                  onChange(...args)
                }
              }}
              canApprove={permissions.approve}
              canUpdate={permissions.update}
              canRemove={permissions.remove}
              isSubmitDisabled={isSubmitDisabled()}
              isManagerOrAdmin={timeoffSupervisor}
              isCurrentApprover={!!isCurrentApprover}
              approvalFlowId={approvalFlowId}
              isLoading={isLoading}
              fromShifts={fromShifts}
              onClose={onClose}
            />
          )
        }
      >
        {loading || leaveDataLoading ? (
          <Loading />
        ) : (
          <>
            <Content
              employee={employee}
              leaveTypes={leaveTypes}
              documentForm={documentForm}
              form={form}
              leave={leave}
              leaveId={leaveId}
              readOnly={permissions.readOnlyFields}
              showEmployeeSelector={false}
              year={year}
              customFieldsForms={customFields.forms}
              onDocumentDropped={onDocumentDropped}
              leaveTypeReadOnly
              electronicSickLeaveEmployee={electronicSickLeaveEmployee}
              leaveConfirmationDetails={leaveConfirmationDetails}
              contractVersions={contractVersions}
              timeTaken={timeTaken}
              labourUnits={labourUnits}
            />
            <Box gap="s24">
              <Box gap="s8">
                {leaveIdAllowance && (
                  <FranceCalendarWarnings
                    allowance={leaveIdAllowance}
                    admin={timeoffSupervisor}
                    labourUnits={labourUnits}
                  />
                )}
                <LeaveInfo form={form} />
                {canDelegateApprover && employee && (
                  <DelegateApproverSelector
                    category="timeoff"
                    approver={delegatedApprover ?? null}
                    employee={employee}
                    field={form.get('delegated_approver_id')}
                    startsOn={form.get('start_on').value}
                    finishOn={form.get('finish_on').value}
                  />
                )}
                {medicalLeave.isMedical && (
                  <Alert color="warning">
                    <Text.Normal>
                      {i18n.t('time_off.medical_leave.not_editable_notice.description')}
                    </Text.Normal>
                    <Link.Href href={helpCenterUrl('new_request')} target="_blank">
                      {i18n.t('time_off.medical_leave.not_editable_notice.link')}
                    </Link.Href>
                  </Alert>
                )}
              </Box>
              <ApprovalsInformationBlock resourceId={leave.id} resourceType="Timeoff::Leave" />
              {rejectionReason && (
                <Text.Normal size="200" color="grey800" overflowWrap>
                  {i18n.t('time_off.rejection_reason')}: {rejectionReason}
                </Text.Normal>
              )}
              {canSeeRequester && (
                <Box>
                  <Text.Normal size="300" color="grey800">
                    {i18n.t('time_off.requester_label')}: {authorName}
                  </Text.Normal>
                </Box>
              )}
            </Box>
          </>
        )}
      </Modal>
    </ExpiredTrialChecker>
  )
}

export default observer(EditLeavePage)
